import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiControlledTextField,
  MiPlaceholderList,
  MiSectionHeader,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ICONS_WEB } from "@miplatform/constants";
import {
  filterMaterialsByAttributes,
  filterMaterialsByCategory,
  filterMaterialsBySearch,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { CategoryFilterDrawer } from "../../../../components/FilterDrawer/CategoryFilterDrawer";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";
import { AdderCard } from "./AdderCard/AdderCard";
import { useAdderDetailFormViewModel } from "./useAdderDetailFormViewModel";

export function AdderDetailForm({
  isReadOnly,
  setIsReadOnly,
  editId,
  control,
  isLoading,
  stateManager,
  setValue,
  getValues,
}) {
  const {
    materials,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    debouncedSearch,
    search,
    filteredMaterials,
    openMaterialResults,
    openCategoryDrawer,
    hasFilters,
    adderItems,
    getMaterials,
    getCategoryFilters,
    getMenuOptions,
    handleSearch,
    handleCategoryClick,
    handleAttributeValueClick,
    handleReset,
    handleResetResultContainer,
    handleCloseCategoryDrawer,
    handleAddRemoveData,
    isDataSelected,
    setFilteredMaterials,
    setOpenCategoryDrawer,
    setAdderItems,
  } = useAdderDetailFormViewModel({
    stateManager,
    setValue,
  });
  const { classes } = useStyles({ openMaterialResults });
  const { materials: adderMaterials = [] } = getValues();

  useEffect(() => {
    getCategoryFilters();
    getMaterials();
  }, []);

  useEffect(() => {
    if (editId) {
      setAdderItems(adderMaterials);
    }
  }, [adderMaterials]);

  useEffect(() => {
    let filtered = [...materials];
    if (selectedCategory?.categoryId) {
      filtered = filterMaterialsByCategory(filtered, selectedCategory);
    }
    if (Object.keys(selectedAttributeValues).length > 0) {
      filtered = filterMaterialsByAttributes(filtered, selectedAttributeValues);
    }
    if (debouncedSearch) {
      filtered = filterMaterialsBySearch(filtered, debouncedSearch);
    }
    setFilteredMaterials(filtered);
  }, [selectedCategory, selectedAttributeValues, debouncedSearch]);

  useEffect(() => {
    if (isReadOnly) {
      handleCloseCategoryDrawer();
    }
  }, [isReadOnly]);

  return (
    <Box className={classes.root}>
      <Box className={classes.adderContainer}>
        <MiBox className={classes.detailBox}>
          <MiSectionHeader
            title={
              editId ? "Adder Bucket Details" : "Enter Adder Bucket Details"
            }
            subtitle={
              !isReadOnly &&
              `${editId ? "Edit" : "Add"} details and adder items below.`
            }
            rightIcon={editId && isReadOnly && mdi[ICONS_WEB.PENCIL_OUTLINE]}
            onRightIconClick={() => setIsReadOnly(false)}
          />
          <MiControlledTextField
            formControl={control}
            name="name"
            label="Adder Bucket Name"
            className={classes.textField}
            required={!isReadOnly}
            inputProps={{ maxLength: 64 }}
            disabled={isReadOnly}
            readOnly={isReadOnly}
          />
        </MiBox>
        <MiBox className={classes.itemBox}>
          <MiSectionHeader
            title="Adder Items"
            rightAction={
              <MiButton
                title="Add Adder Items"
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
                onClick={() => setOpenCategoryDrawer(true)}
                disabled={openCategoryDrawer || isReadOnly}
              />
            }
          />
          {adderItems.map((data) => (
            <AdderCard
              key={data.id}
              data={data}
              isReadOnly={isReadOnly}
              getMenuOptions={getMenuOptions}
              stateManager={stateManager}
              getValues={getValues}
            />
          ))}
          {adderItems.length === 0 && (
            <MiPlaceholderList
              text="No adder items added."
              helperText="To add, click + Add Adder Items button."
            />
          )}
        </MiBox>
      </Box>
      <SearchResultsContainer
        open={openMaterialResults}
        onClose={handleResetResultContainer}
        alertTitle="Add Item(s) to Adder Items."
        alertDescription="Click the Add Item Button to add item(s) to the adder."
        storageKey="adderBucket-alert"
        isLoading={isLoading}
        results={filteredMaterials}
        hasFilters={hasFilters}
        handleResetFilterSelection={handleReset}
        renderResults={() =>
          filteredMaterials.map((material) => {
            const isSelected = isDataSelected(material);
            return (
              <MaterialCard
                key={material.id}
                item={material}
                isSelected={isSelected}
                rightAction={
                  <MiButton
                    title={isSelected ? "Remove Item" : "Add Item"}
                    variant={BUTTON_VARIANTS.CONTAINED}
                    color={isSelected ? "error" : "primary"}
                    onClick={() => handleAddRemoveData(material)}
                  />
                }
              />
            );
          })
        }
      />
      <CategoryFilterDrawer
        open={openCategoryDrawer}
        onClose={handleCloseCategoryDrawer}
        title="Filter Items"
        categoryFilterOptions={categoryFilterOptions}
        search={search}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        setSearch={handleSearch}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        handleReset={handleReset}
        handleResetSelection={handleReset}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme, { openMaterialResults }) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  adderContainer: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  detailBox: {
    margin: theme.spacing(3, 3, 0, 3),
    flex: 0.3,
  },
  textField: {
    width: openMaterialResults ? "100%" : "50%",
  },
  itemBox: {
    flex: 1,
  },
}));
